import { QuoteChargeListingEntity } from '../types';
import { BasePrice } from '../../equipment/types';
import { DiscountType, QuoteFeeType } from '@trnsact/trnsact-shared-types';

class QuoteChargeCalculator {
  public calc(input: QuoteChargeListingEntity[]): QuoteChargeListingEntity[] {
    return input.map(entity => this.calculateEntity(entity));
  }

  private calculateEntity(entity: QuoteChargeListingEntity): QuoteChargeListingEntity {
    const result = { ...entity };

    if (result.charge?.price) {
      result.charge.price = this.calculatePrice(result.charge.price);
    }

    if (result.equipment?.price) {
      result.equipment.price = this.calculatePrice(result.equipment.price);

      if (result.equipment.attachments?.length > 0) {
        result.equipment.attachments = result.equipment.attachments.map(attachment => ({
          ...attachment,
          price: this.calculatePrice(attachment.price),
        }));
      }
    }

    return result;
  }

  private calculatePrice(price: BasePrice): BasePrice {
    const {
      wholesalePrice,
      retailPrice: originalRetailPrice,
      markup,
      markupType,
      discount,
      discountType,
      salesTax,
      salesTaxType,
    } = price;

    const validWholesalePrice = wholesalePrice || 0;

    const markupAmount = markupType === DiscountType.Percent ? validWholesalePrice * (markup / 100) : markup;
    const retailPrice = validWholesalePrice + markupAmount || originalRetailPrice;

    const discountAmount = discountType === DiscountType.Percent ? retailPrice * (discount / 100) : discount;
    const netPrice = retailPrice - discountAmount;

    const taxAmount = salesTaxType === DiscountType.Percent ? netPrice * (salesTax / 100) : salesTax;

    const total = netPrice + taxAmount;

    return {
      total,
      markup,
      discount,
      netPrice,
      markupType,
      retailPrice,
      discountType,
      salesTaxType,
      salesTax: taxAmount,
      wholesalePrice: validWholesalePrice,
    };
  }

  public getTotalAmount(entities: QuoteChargeListingEntity[]): number {
    return entities.reduce((sum, entity) => {
      let entityTotal = 0;

      if (entity.charge?.price?.total) {
        entityTotal += entity.charge.price.total;
      }

      if (entity.equipment?.price?.total) {
        entityTotal += entity.equipment.price.total;

        if (entity.equipment.attachments?.length > 0) {
          entity.equipment.attachments.forEach(attachment => {
            if (attachment.price?.total) {
              entityTotal += attachment.price.total;
            }
          });
        }
      }

      return sum + entityTotal;
    }, 0);
  }

  public getColumnTotals(entities: QuoteChargeListingEntity[]): ColumnTotals {
    const totals: ColumnTotals = {
      wholesalePrice: 0,
      markup: 0,
      retailPrice: 0,
      discount: 0,
      netPrice: 0,
      salesTax: 0,
      total: 0,
    };

    entities.forEach(entity => {
      if (entity.charge?.price) {
        this.addPriceToTotals(totals, entity.charge.price);
      }

      if (entity.equipment?.price) {
        this.addPriceToTotals(totals, entity.equipment.price);

        if (entity.equipment.attachments?.length > 0) {
          entity.equipment.attachments.forEach(attachment => {
            if (attachment.price) {
              this.addPriceToTotals(totals, attachment.price);
            }
          });
        }
      }
    });

    return totals;
  }

  private addPriceToTotals(totals: ColumnTotals, price: BasePrice): void {
    totals.wholesalePrice += price.wholesalePrice;

    const markupAmount =
      price.markupType === DiscountType.Percent ? price.wholesalePrice * (price.markup / 100) : price.markup;
    totals.markup += markupAmount;

    totals.retailPrice += price.retailPrice;

    const discountAmount =
      price.discountType === DiscountType.Percent ? price.retailPrice * (price.discount / 100) : price.discount;
    totals.discount += discountAmount;

    totals.netPrice += price.netPrice;
    totals.salesTax += price.salesTax;
    totals.total += price.total;
  }

  public getGrandTotal(entities: QuoteChargeListingEntity[]): number {
    const columnTotals = this.getColumnTotals(entities);
    return columnTotals.total;
  }

  public getDetailedTable(entities: QuoteChargeListingEntity[]): DetailedTable {
    const rows: any[] = [];

    entities.forEach(entity => {
      if (entity.charge) {
        rows.push({
          label: entity.charge.label,
          type: 'charge',
          feeType: entity.charge.type,
          ...this.extractPriceValues(entity.charge.price),
        });
      }

      if (entity.equipment?.price) {
        rows.push({
          label: 'Equipment',
          type: 'equipment',
          ...this.extractPriceValues(entity.equipment.price),
        });

        entity.equipment.attachments?.forEach((attachment, index) => {
          if (attachment.price) {
            rows.push({
              label: `Attachment ${index + 1}`,
              type: 'attachment',
              ...this.extractPriceValues(attachment.price),
            });
          }
        });
      }
    });

    const columnTotals = this.getColumnTotals(entities);

    return {
      rows,
      columnTotals,
      grandTotal: columnTotals.total,
    };
  }

  private extractPriceValues(price: BasePrice): PriceValues {
    const markupAmount =
      price.markupType === DiscountType.Percent ? price.wholesalePrice * (price.markup / 100) : price.markup;

    const discountAmount =
      price.discountType === DiscountType.Percent ? price.retailPrice * (price.discount / 100) : price.discount;

    return {
      wholesalePrice: price.wholesalePrice,
      markup: markupAmount,
      retailPrice: price.retailPrice,
      discount: discountAmount,
      netPrice: price.netPrice,
      salesTax: price.salesTax,
      total: price.total,
    };
  }

  public getBreakdown(entities: QuoteChargeListingEntity[]) {
    const breakdown = {
      subtotal: 0,
      taxes: 0,
      fees: 0,
      total: 0,
      items: [],
    };

    entities.forEach(entity => {
      if (entity.charge) {
        const item = {
          label: entity.charge.label,
          type: entity.charge.type,
          amount: entity.charge.price.total,
        };
        // @ts-ignore
        breakdown.items.push(item);

        if (this.isTaxType(entity.charge.type)) {
          breakdown.taxes += item.amount;
        } else {
          breakdown.fees += item.amount;
        }
      }

      if (entity.equipment?.price) {
        breakdown.subtotal += entity.equipment.price.total;

        entity.equipment.attachments?.forEach(attachment => {
          if (attachment.price?.total) {
            breakdown.subtotal += attachment.price.total;
          }
        });
      }
    });

    breakdown.total = breakdown.subtotal + breakdown.taxes + breakdown.fees;
    return breakdown;
  }

  private isTaxType(type: QuoteFeeType): boolean {
    return [
      QuoteFeeType.CityTax,
      QuoteFeeType.CountyTax,
      QuoteFeeType.StateTax,
      QuoteFeeType.FederalExciseTax,
      QuoteFeeType.VehicleInventoryTax,
    ].includes(type);
  }
}

export interface ColumnTotals {
  wholesalePrice: number;
  markup: number;
  retailPrice: number;
  discount: number;
  netPrice: number;
  salesTax: number;
  total: number;
}

export interface PriceValues {
  wholesalePrice: number;
  markup: number;
  retailPrice: number;
  discount: number;
  netPrice: number;
  salesTax: number;
  total: number;
}

export interface DetailedTable {
  rows: any[];
  columnTotals: ColumnTotals;
  grandTotal: number;
}

export const quoteChargeCalculator = new QuoteChargeCalculator();
